import { useQueries } from 'react-query';
import { StaffAvailabilityCalendarEvent, UrlParams } from 'types';
import { client } from 'utils/api-client';

interface UseStaffAvailabilityCalendarParallelProps {
  coachIds: string[];
  startDate: string | null;
  endDate: string | null;
  enabled?: boolean;
}

export interface StaffAvailabilityCalendarEventWithResource extends StaffAvailabilityCalendarEvent {
  resource: string;
}

interface UseStaffAvailabilityCalendarParallelReturn {
  data: StaffAvailabilityCalendarEventWithResource[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

const QUERY_KEY_NAME = 'staff-availability-calendar';

export default function useStaffAvailabilityCalendarParallel({
  coachIds,
  startDate,
  endDate,
  enabled = true,
}: UseStaffAvailabilityCalendarParallelProps): UseStaffAvailabilityCalendarParallelReturn {
  const validCoachIds = coachIds.filter(id => id && id !== 'None');

  const queries = useQueries(
    validCoachIds.map(coachId => ({
      queryKey: [QUERY_KEY_NAME, coachId, startDate, endDate],
      queryFn: async () => {
        const params: UrlParams = {
          coaches: coachId,
        };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await client.get<StaffAvailabilityCalendarEvent[]>('staff-availability/calendar/', {
          params,
        });

        return response.data.map(event => ({
          ...event,
          resource: coachId,
        }));
      },
      enabled: enabled && !!startDate && !!endDate,
    })),
  );

  const isLoading = queries.some(q => q.isLoading);
  const isError = queries.some(q => q.isError);
  const isSuccess = validCoachIds.length > 0 && queries.every(q => q.isSuccess);

  const data = queries.reduce<StaffAvailabilityCalendarEventWithResource[]>((acc, q) => {
    if (q.isSuccess && q.data) acc.push(...q.data);
    return acc;
  }, []);

  return { data, isLoading, isError, isSuccess };
}
